import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminSearchField,
  AdminListFilterDropdown,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminRowActionsButton,
  AdminLinkAction,
  AdminHeaderAction,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useAuth from '../../../Hooks/useAuth';
import useClickOutside from '../../../Hooks/useClickOutside';
import GenerateReportModal from './GenerateReportModal';
import {
  FREQUENCY,
  formatDateDisplay,
  nextGeneratedId,
  getAllReportsMerged,
  saveExtraReport,
} from './adminReportsData';
import { triggerPdfDownload } from './pdfDownload';

/** Explicit tracks so header and body columns share the same geometry (matches Customers grid style). */
const REPORTS_GRID_COLUMNS =
  'minmax(160px, 2fr) minmax(120px, 1.25fr) minmax(96px, 0.75fr) minmax(140px, 1.25fr) minmax(140px, 1.25fr) minmax(108px, 1fr) minmax(140px, 1.5fr) minmax(56px, 0.5fr) minmax(128px, auto)';

/** Approximate height of row actions menu (3 items + padding) for viewport flip / fixed position. */
const ROW_ACTIONS_MENU_HEIGHT = 132;

const formatDayMonYear = (dateLike) => {
  if (!dateLike) return '—';
  if (dateLike instanceof Date && !Number.isNaN(dateLike.getTime())) {
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(dateLike);
  }
  const s = String(dateLike).trim();
  if (!s || s === '—') return '—';

  // If it's month+year (e.g. "January 2026"), treat it as first of month.
  const monthYearMatch = s.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const [, monthName, year] = monthYearMatch;
    const parsed = Date.parse(`${monthName} 01, ${year}`);
    if (!Number.isNaN(parsed)) return formatDayMonYear(new Date(parsed));
  }

  const parsed = Date.parse(s);
  if (Number.isNaN(parsed)) return s; // fallback: keep as-is if we can't parse
  return formatDayMonYear(new Date(parsed));
};

const splitPeriod = (period) => {
  if (!period) return { from: '—', to: '—' };
  const s = String(period).trim();
  if (!s) return { from: '—', to: '—' };

  // Range like: "01 Jan 2026 - 15 Jan 2026"
  const parts = s.split(' - ');
  if (parts.length >= 2) {
    return {
      from: formatDayMonYear(parts[0]),
      to: formatDayMonYear(parts.slice(1).join(' - ')),
    };
  }

  // Month-only like: "January 2026" → 1st day to last day of month.
  const monthYearMatch = s.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const [, monthName, year] = monthYearMatch;
    const firstParsed = Date.parse(`${monthName} 01, ${year}`);
    if (!Number.isNaN(firstParsed)) {
      const first = new Date(firstParsed);
      const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
      return { from: formatDayMonYear(first), to: formatDayMonYear(last) };
    }
  }

  // Single-day / unknown format: show same value in both columns.
  const asDate = formatDayMonYear(s);
  return { from: asDate, to: asDate };
};

const generatedByName = (generatedBy) => {
  if (!generatedBy) return '';
  const s = String(generatedBy);
  // legacy data uses: "<module> — <name>"
  const parts = s.split(' — ');
  return (parts[parts.length - 1] || s).trim();
};

const REPORT_AREA_OPTIONS = [
  ['', 'All'],
  ['Platform', 'Platform'],
  ['Moderation', 'Moderation'],
  ['Compliance', 'Compliance'],
  ['Fraud & Security', 'Fraud & Security'],
  ['Support Tickets', 'Support Tickets'],
  ['Billing & Subscriptions', 'Billing & Subscriptions'],
  ['Customers', 'Customers'],
  ['Businesses', 'Businesses'],
  ['Posts', 'Posts'],
  ['Reviews', 'Reviews'],
];

export default function AdminReportsList() {
  const navigate = useNavigate();
  const location = useLocation();

  const openReport = (row) => {
    navigate(`/admin/reports/${row.id}`, { state: { from: location.pathname } });
  };
  const { auth } = useAuth();
  const [rows, setRows] = useState(() => getAllReportsMerged());
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [areaOpen, setAreaOpen] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const areaRef = useRef(null);
  const periodRef = useRef(null);
  const [reportRowActionsOpen, setReportRowActionsOpen] = useState({});
  const reportRowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const [rowActionsMenuLayout, setRowActionsMenuLayout] = useState(null);

  const toggleReportRowActions = (rowKey) => {
    setReportRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(reportRowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setReportRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openReportActionsRowId = useMemo(
    () => Object.keys(reportRowActionsOpen).find((k) => reportRowActionsOpen[k]),
    [reportRowActionsOpen]
  );

  useLayoutEffect(() => {
    if (!openReportActionsRowId) {
      setRowActionsMenuLayout(null);
      return;
    }
    const el = reportRowActionsRefs.current[openReportActionsRowId];
    if (!el) {
      setRowActionsMenuLayout(null);
      return;
    }
    const updateLayout = () => {
      const r = el.getBoundingClientRect();
      const gap = 4;
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < ROW_ACTIONS_MENU_HEIGHT + gap;
      const topRaw = openUp ? r.top - ROW_ACTIONS_MENU_HEIGHT - gap : r.bottom + gap;
      const top = Math.min(
        Math.max(gap, topRaw),
        window.innerHeight - ROW_ACTIONS_MENU_HEIGHT - gap
      );
      setRowActionsMenuLayout({
        top,
        right: window.innerWidth - r.right,
      });
    };
    updateLayout();
    const tableEl = tableScrollRef.current;
    window.addEventListener('resize', updateLayout);
    window.addEventListener('scroll', updateLayout, true);
    tableEl?.addEventListener('scroll', updateLayout, { passive: true });
    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('scroll', updateLayout, true);
      tableEl?.removeEventListener('scroll', updateLayout);
    };
  }, [openReportActionsRowId]);

  useClickOutside(areaRef, () => setAreaOpen(false));
  useClickOutside(periodRef, () => setPeriodOpen(false));

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, []);

  const periodOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.reportPeriod));
    return ['', ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (areaFilter && r.reportArea !== areaFilter) return false;
      if (periodFilter && r.reportPeriod !== periodFilter) return false;
      if (!q) return true;
      return (
        r.reportName.toLowerCase().includes(q) ||
        r.reportArea.toLowerCase().includes(q) ||
        generatedByName(r.generatedBy).toLowerCase().includes(q)
      );
    });
  }, [rows, search, areaFilter, periodFilter]);

  const displayName =
    auth?.user?.businessName || auth?.user?.name || auth?.user?.email || 'Team Member';

  const handleGenerate = ({ reportArea, reportName, dateFrom, dateTo, notes }) => {
    const id = nextGeneratedId();
    const fromD = formatDateDisplay(dateFrom);
    const toD = formatDateDisplay(dateTo);
    const generatedBy = displayName;
    const newRow = {
      id,
      reportName,
      reportArea,
      frequency: FREQUENCY.ON_DEMAND,
      reportPeriod: `${fromD} - ${toD}`,
      generatedOn: new Date().toISOString().slice(0, 10),
      generatedBy,
      format: 'PDF',
      status: 'Ready',
      notes: notes || undefined,
      template: reportArea === 'Moderation' ? 'moderation-summary' : 'generic',
    };
    saveExtraReport(newRow);
    setRows(getAllReportsMerged());
  };

  const handleDownload = (row) => {
    triggerPdfDownload(row.id);
  };

  const periodFilterOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...periodOptions.filter(Boolean).map((period) => ({ value: period, label: period })),
    ],
    [periodOptions],
  );
  const areaFilterOptions = REPORT_AREA_OPTIONS.map(([value, label]) => ({
    value,
    label,
  }));

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading reports...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Reports</AdminPageTitle>
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="w-full max-w-[450px]">
              <AdminSearchField
                placeholder="Report name, module, generated by"
                title="Search reports"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Module"
                value={areaFilter}
                options={areaFilterOptions}
                onChange={setAreaFilter}
                menuRef={areaRef}
                open={areaOpen}
                setOpen={setAreaOpen}
              />
              <AdminListFilterDropdown
                label="Report Period"
                value={periodFilter}
                options={periodFilterOptions}
                onChange={setPeriodFilter}
                menuRef={periodRef}
                open={periodOpen}
                setOpen={setPeriodOpen}
              />
              <AdminHeaderAction className="!h-[36px] shrink-0" onClick={() => setGenerateOpen(true)}>
                Generate Report
              </AdminHeaderAction>
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div
              className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4 items-center min-w-[1250px]"
              style={{ gridTemplateColumns: REPORTS_GRID_COLUMNS }}
            >
              <AdminTableHeader className="min-w-0">Report Name</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Module</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Frequency</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Period From</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Period To</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Generated On</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Generated By</AdminTableHeader>
              <AdminTableHeader className="min-w-0">Format</AdminTableHeader>
              <div className="flex items-center justify-center min-w-0">
                <AdminTableHeader>Actions</AdminTableHeader>
              </div>
            </div>
            {filtered.map((row) => (
              <div
                key={row.id}
                className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center min-w-[1250px] cursor-pointer"
                style={{ gridTemplateColumns: REPORTS_GRID_COLUMNS }}
                onClick={() => openReport(row)}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="truncate" title={row.reportName}>
                    {row.reportName}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="truncate" title={row.reportArea}>
                    {row.reportArea}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="whitespace-nowrap">{row.frequency}</AdminTableBody>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableMeta className="whitespace-nowrap">
                    {splitPeriod(row.reportPeriod).from}
                  </AdminTableMeta>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableMeta className="whitespace-nowrap">
                    {splitPeriod(row.reportPeriod).to}
                  </AdminTableMeta>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableMeta className="whitespace-nowrap">{formatDateDisplay(row.generatedOn)}</AdminTableMeta>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="truncate" title={generatedByName(row.generatedBy)}>
                    {generatedByName(row.generatedBy)}
                  </AdminTableBody>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableBody className="whitespace-nowrap">{row.format}</AdminTableBody>
                </div>
                <div
                  className="flex items-center justify-center relative min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="relative inline-flex"
                    ref={(el) => {
                      reportRowActionsRefs.current[row.id] = el;
                    }}
                  >
                    <AdminRowActionsButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleReportRowActions(row.id);
                      }}
                      title="Actions"
                      aria-label="Row actions"
                    >
                      <AdminRowMenuIcon />
                    </AdminRowActionsButton>
                    {reportRowActionsOpen[row.id] && rowActionsMenuLayout && (
                      <div
                        className="fixed z-[200] min-w-[200px] rounded-lg border border-lightgray bg-white py-2 shadow-lg"
                        style={{
                          top: rowActionsMenuLayout.top,
                          right: rowActionsMenuLayout.right,
                        }}
                      >
                        <AdminLinkAction
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReport(row);
                            setReportRowActionsOpen({});
                          }}
                          className="block w-full px-3 py-1.5 text-left"
                        >
                          View
                        </AdminLinkAction>
                        <AdminLinkAction
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(row);
                            setReportRowActionsOpen({});
                          }}
                          className="block w-full px-3 py-1.5 text-left"
                        >
                          Download
                        </AdminLinkAction>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 ? (
              <div className="py-10 text-center">
                <AdminSupportingText className="text-mediumgray">No reports match your filters.</AdminSupportingText>
              </div>
            ) : null}
          </div>
        </DashboardMainContainer>
      </div>

      <GenerateReportModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerate={handleGenerate}
      />
    </AdminLayout>
  );
}
